import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, ValidationErrors, FormGroup, FormControl, Validators } from '@angular/forms';
// Services
import { MediaQueryService, SnackBarService, SnackBarTypes } from '@my7n/ui';
import { COMMON_BUTTONS_TEXTS } from '../../../interfaces/common-texts';
import {
  RequestEmailUpdateStatus,
  MAX_LENGTHS,
  ErrorMessages,
  RequestEmailUpdateErrors,
} from '../../../interfaces/user-profile';
import { EmailValidator } from '../../../validators/email-validator';
import { UserProfileService } from '../../../services/user-profile.service';
import { RequiredTrimmedValidator } from '../../../validators/required-trimmed.validator';

@Component({
  selector: 'email-edit',
  templateUrl: './email-edit.component.html',
  styleUrls: ['./email-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserProfileService],
  standalone: false,
})
export class EmailEditComponent implements OnInit {
  @Input() consultantId: string;
  @Input() email: string;
  @Input() readonly = false;
  @Input() sidePanel = false;
  @Input() canEdit = true;

  @Output() serverError = new EventEmitter<boolean>();
  @Output() updateSuccess = new EventEmitter<string>();

  emailEditForm: FormGroup<{
    email: FormControl<string>;
  }>;

  COMMON_BUTTONS_TEXTS = COMMON_BUTTONS_TEXTS;
  MAX_EMAIL_LENGTH = MAX_LENGTHS.email;
  ERRORS = ErrorMessages;

  editMode = false;
  error = false;
  loading = false;

  _copiedEmail: string;

  currentStatus: RequestEmailUpdateStatus = null;

  constructor(
    public mediaQueryService: MediaQueryService,
    private changeDetRef: ChangeDetectorRef,
    private userProfileService: UserProfileService,
    private snackbarService: SnackBarService
  ) {}

  ngOnInit() {
    this.emailEditForm = new FormGroup({
      email: new FormControl(this.email, [
        RequiredTrimmedValidator,
        Validators.maxLength(this.MAX_EMAIL_LENGTH),
        EmailValidator,
      ]),
    });
  }

  activate() {
    if (!this.editMode && !this.readonly) {
      this.editMode = true;
      this.error = false;
      this._copiedEmail = this.emailEditForm.value.email;
    }
  }

  keyListener($event) {
    switch ($event.which) {
      // Bind ESC key
      case 27:
        this.reset();
        break;
    }
  }

  reset() {
    this.emailEditForm.reset({
      email: this._copiedEmail,
    });

    this.email = this._copiedEmail;
    this.loading = false;
    this.editMode = false;
    this.error = false;
    this.currentStatus = null;
  }

  update() {
    if (this.loading || this.readonly) {
      console.debug('[EmailEditComponent] Cannot save during loading or while in readonly mode');
      return false;
    }

    if (!this.canEdit) {
      console.debug("[EmailEditComponent] User doesn't have permission to edit");
      return false;
    }

    if (this.emailEditForm.invalid) {
      console.debug('[EmailEditComponent] Form is invalid, value:', this.emailEditForm.value);
      return false;
    }

    this.error = false;
    this.loading = true;

    this.emailEditForm.controls.email.setValue(this.getTrimmed(this.emailEditForm.controls.email.value));
    const email = this.emailEditForm.value.email;

    this.userProfileService.requestEmailUpdateById(email, this.consultantId).subscribe({
      next: (response) => {
        if (RequestEmailUpdateErrors.includes(response.Status)) {
          this.errorHandler(false);
          this.currentStatus = response.Status;
          return;
        }

        this.error = false;
        this.editMode = false;
        this.email = email;
        this.currentStatus = response.Status;
        this.updateSuccess.emit(this.email);
        this.snackbarService.open({
          message: `Email has been changed to: ${this.email}.`,
          type: SnackBarTypes.SuccessAlt,
        });
      },
      error: () => {
        this.errorHandler();
        this.currentStatus = RequestEmailUpdateStatus.RequestFailed;
      },
      complete: () => {
        const validationErrors = this.backendValidation(this.emailEditForm.get('email'));

        this.loading = false;
        this.emailEditForm.get('email').setErrors(validationErrors);
        this.changeDetRef.markForCheck();
      },
    });
  }

  private errorHandler(emit = true) {
    this.error = true;
    if (emit) {
      this.serverError.emit(true);
    }
  }

  private getTrimmed(value: string): string | null {
    return value ? value.trim() : null;
  }

  private backendValidation(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const status = this.currentStatus;
    if (status) {
      return {
        [status]: true,
      };
    }

    return null;
  }
}
